/**
 * AtlasCaveModule — EXPLORABLE CAVES (terrain wave 2026-09-08).
 *
 * Sites come from shared/caves.ts (pure, mirrored); the chunk assembler cuts
 * the mouth out of the terrain mesh + collider; this module owns everything
 * INSIDE: the marching-cubes cavity from cave_worker (a Perlin worm heading
 * uphill into the hill, two chambers), its trimesh collider, a rock material
 * (triplanar highland texture), glowing crystals along the walls, one shared
 * point light that follows the player to the nearest chamber, and a boulder
 * collar around the mouth that hides the coarse edge of the terrain hole.
 *
 * Grounding law: while the player is INSIDE a cavity (`atlas.caveId`), the
 * physics terrain clamp yields exactly as it does for a building interior —
 * the cave's trimesh is the floor authority, the analytic ground above is not.
 *
 * Levers: window.__caves() → sites near, built interiors, the player's cave.
 * localStorage 'atlas:caves' = 'off' disables the layer (mouths stay cut).
 *
 * CAVE LIGHT (shader gaps program, 2026-09-29; lib/cave_light*.ts): the worker also bakes each cave's probe grid —
 * the SKY TRANSFER (how much of the live sky reaches each point, one bounce included) and the crystals' LOCAL
 * irradiance — posted after the mesh. The rock, boulders and crystals then swap (after a warm compile) to materials
 * whose lighting model takes the hemisphere + sky IBL only through that transfer and adds the crystals' light; the
 * field fades in over FIELD_FADE_S. On med+ one CubeCamera capture per cave (its local radiance, no sky) is PMREM'd
 * and box-projected against the largest chamber for the crystals' reflections. While the player is inside, the
 * global hemisphere + IBL take the field at the player (the hook, eased; LightingModule applies it). Tier flag
 * `caveLight`; kill switch localStorage 'atlas:cave-light' = 'off'; lever window.__caveLight().
 */
import * as THREE from 'three/webgpu';
import type { App, IModule } from 'enginex';
import { makeSystem, Phase } from '../engine-compat.js';
import { atlas } from '../state.js';
import { postRiverTable } from '../lib/hydrology_load.js';
import { disposeLater } from '../lib/gpu_dispose.js';
import { caveSites, type CaveSite } from '@shared/caves.js';
import { terrainHeight } from '@shared/terrain.js';
import { caveDensity, portalDome, CAVE_PORTAL, MOUTH_R, type CavePlan, type CaveCapsule } from '../lib/cave_gen.js';
import type { CaveJob, CaveResult } from '../workers/cave_worker.js';
import type { AssetModule } from './AssetModule.js';
import type { PhysicsModule } from './PhysicsModule.js';
import { BIOME_GROUND } from '../config.js';
import { pinStaticMotionVectors } from 'enginex/render/batching/staticMotionVectors.js';
import { RAIN_SHELTER_LAYER } from '../lib/rain_shelter.js';
import { activePreset } from '../lib/quality_tiers.js';
import { crystalPlacements, crystalRadiance, CRYSTAL_EMISSIVE_HEX, CRYSTAL_EMISSIVE_INTENSITY } from '../lib/cave_light_bake.js';
import { halfFieldView, hookTargetAt, sampleField, type CaveLightStats } from '../lib/cave_light.js';
import {
  createFieldGpu, createCaveRockMaterial, createCaveCrystalMaterial, captureCaveProbe, createProbeGpu, rebaseCaveUniforms,
  caveRockAlbedo, caveLightMaterialStats, type CaveFieldGpu, type CaveProbeGpu, type CaveLitMaterial,
} from '../lib/cave_light_material.js';
import { setCaveHookTarget, caveHookState } from '../lib/cave_light_hook.js';
import type { CaveLightResult } from '../workers/cave_worker.js';

const TICK_EVERY = 10;
/** Build interiors within this range of the player; drop beyond DROP_R. */
const BUILD_R = 900;
const DROP_R = 1300;
/** The light field fades in (and out) over this long (s): the exposure meter never sees a step. */
const FIELD_FADE_S = 1.2;
/** A capture waits this many ticks for the rock texture, then takes the flat rock. */
const CAPTURE_WAIT_TICKS = 60;
/** A warm compile gives up after this long and swaps anyway (ms). */
const WARM_TIMEOUT_MS = 4000;

type CaveLightMode = 'off' | 'visibility' | 'baked' | 'baked+probe';

/** One cave's consumed light field. */
interface LitCave {
  gpu: CaveFieldGpu;
  /** the field origin in ABSOLUTE world space (site + the mouth-relative origin) */
  originAbs: [number, number, number];
  view: ReturnType<typeof halfFieldView>;
  rock: CaveLitMaterial;
  crystal: CaveLitMaterial;
  probe: CaveProbeGpu | null;
  probeState: 'none' | 'pending' | 'done' | 'failed';
  probeWaited: number;
  mix: number; local: number; probeW: number;
  warmed: boolean;
  bakeMs: number; captureMs: number; stats: CaveLightStats;
}
/** The entrance light comes on within this range of a built mouth (m). */
const MOUTH_LIGHT_M = 18;

interface Built {
  group: THREE.Group;
  colliderId: string;
  caps: CaveCapsule[];
  chambers: Array<{ x: number; y: number; z: number; r: number }>;
  site: CaveSite;
  mesh: THREE.Mesh;
  crystals: THREE.InstancedMesh;
  boulders: THREE.InstancedMesh;
  /** local (mouth-relative) crystal matrices — the probe capture draws them */
  crystalMatrices: THREE.Matrix4[];
  light: LitCave | null;
  lightPending: boolean;
}

export class AtlasCaveModule implements IModule {
  readonly name = 'AtlasCaveModule';
  private scene!: THREE.Scene;
  private seed = '';
  private worker: Worker | null = null;
  private pending = new Set<number>();
  private built = new Map<number, Built>();
  private rockMat: THREE.MeshStandardNodeMaterial | null = null;
  private crystalMat: THREE.MeshStandardNodeMaterial | null = null;
  private light: THREE.PointLight | null = null;
  private frame = 0;
  private stats = { requested: 0, builtMs: 0, lastTriangles: 0, lightBaked: 0, lightMs: 0 };
  private app: App | null = null;
  /** the rock's triplanar albedo node (shared by the plain and the lit rock) once the texture lands */
  private rockAlbedo: ReturnType<typeof caveRockAlbedo> | null = null;
  private lightMode: CaveLightMode = 'off';
  private lastFrameMs = 0;
  /** sites whose in-flight build job also asked for the light field */
  private requestedLight = new Set<number>();

  constructor(private readonly assets: AssetModule, private readonly physics: PhysicsModule) {}

  init(app: App): void {
    this.scene = app.world.scene as unknown as THREE.Scene;
    this.rockMat = new THREE.MeshStandardNodeMaterial({ color: 0x5a5148, roughness: 0.92, metalness: 0.02, side: THREE.DoubleSide });
    this.app = app;
    this.lightMode = this.currentLightMode();
    this.crystalMat = new THREE.MeshStandardNodeMaterial({ color: 0x8fd8ff, emissive: CRYSTAL_EMISSIVE_HEX, emissiveIntensity: CRYSTAL_EMISSIVE_INTENSITY, roughness: 0.25, metalness: 0.1 });
    this.light = new THREE.PointLight(0x7fc8ff, 0, 34, 1.6);
    this.light.visible = false;
    this.scene.add(this.light);
    void this.assets.loadTexture(BIOME_GROUND.rocky_highlands).then((tex) => {
      if (!this.rockMat) return;
      tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
      // Triplanar rock: the cavity has no UVs; project the highland texture along
      // the dominant normal axis (blend weights = |n|^4, normalised) — darker,
      // cooler inside (lib/cave_light_material.ts caveRockAlbedo, shared with the lit rock).
      this.rockAlbedo = caveRockAlbedo(tex);
      this.rockMat.colorNode = this.rockAlbedo;
      this.rockMat.needsUpdate = true;
      for (const b of this.built.values()) if (b.light) { b.light.rock.colorNode = this.rockAlbedo; b.light.rock.needsUpdate = true; }
    }).catch(() => { /* flat rock colour stays */ });
    this.physics.setCaveTest((x, y, z) => this.cavityAt(x, y, z));
    app.world.addSystem(makeSystem('AtlasCaves', Phase.RenderSub, 42, () => { this.lightFrame(); this.tick(); }));
    this.installLightLevers();
    (globalThis as unknown as { __caveSurface?: (x: number, z: number) => number }).__caveSurface = (x, z) => this.seed ? terrainHeight(this.seed, x, z) : 0;
    // Probe lever: stand the avatar on the cavity floor at walk point k of a built cave.
    (globalThis as unknown as { __caveEnter?: (id: number, k: number) => unknown }).__caveEnter = (id, k) => {
      const b = this.built.get(id);
      if (!b) return null;
      const pts = capsPath(b);
      const p = pts[Math.max(0, Math.min(pts.length - 1, k))];
      if (!p) return null;
      const feet = p.y - 1.25 + 0.35;
      this.physics.placeFeet(p.x, feet, p.z);
      return { x: p.x, z: p.z, feet, floor: p.y - 1.25, surface: this.seed ? terrainHeight(this.seed, p.x, p.z) : null };
    };
    // Probe lever: the PORTAL ROOF census of a built cave — rays cast straight
    // down over the outcrop's footprint against the cave mesh AND the terrain
    // chunks (the meshes carrying `biomeWA`): every ray must land on
    // something (a miss = a hole into the void), the mouth zone must land on
    // the CAVE mesh (its outcrop roofs the ground's jagged hole), and a ray
    // along the walk axis from the porch must run ≥ 6 m before it meets rock
    // (the arch is open).
    (globalThis as unknown as { __cavePortal?: (id: number) => unknown }).__cavePortal = (id) => {
      const b = this.built.get(id);
      if (!b) return null;
      const s = b.site;
      const caveMeshes: THREE.Object3D[] = [];
      b.group.traverse((o) => { if ((o as THREE.Mesh).isMesh && !(o as THREE.InstancedMesh).isInstancedMesh) caveMeshes.push(o); });
      const terrain: THREE.Object3D[] = [];
      this.scene.traverse((o) => { const m = o as THREE.Mesh; if (m.isMesh && m.geometry?.attributes?.biomeWA && m.visible) terrain.push(m); });
      const ray = new THREE.Raycaster();
      ray.far = 200;
      const shift = this.scene.position; // the FP64 rebase: rays live in render space
      let samples = 0, holes = 0, onCave = 0, onTerrain = 0, mouthOnCave = 0, mouthSamples = 0;
      const holeList: number[][] = [];
      for (let u = -CAVE_PORTAL.a * 1.2 + CAVE_PORTAL.u0; u <= CAVE_PORTAL.a * 1.2 + CAVE_PORTAL.u0; u += 1) {
        for (let v = -CAVE_PORTAL.b * 1.2; v <= CAVE_PORTAL.b * 1.2; v += 1) {
          const x = s.dx * u - s.dz * v, z = s.dz * u + s.dx * v;
          if (portalDome(s, x, z) <= 0) continue;
          samples++;
          const top = terrainHeight(this.seed, s.x + x, s.z + z) + 40;
          ray.set(new THREE.Vector3(s.x + x + shift.x, top + shift.y, s.z + z + shift.z), new THREE.Vector3(0, -1, 0));
          const hc = ray.intersectObjects(caveMeshes, true)[0];
          const ht = ray.intersectObjects(terrain, false)[0];
          const inMouth = u >= -2 && u <= 13 && Math.abs(v) <= 2;
          if (inMouth) mouthSamples++;
          if (!hc && !ht) { holes++; if (holeList.length < 12) holeList.push([Math.round(u), Math.round(v)]); continue; }
          if (hc && (!ht || hc.distance <= ht.distance)) { onCave++; if (inMouth) mouthOnCave++; } else onTerrain++;
        }
      }
      // The arch: from the porch along the heading at doorstep + 1.2 m.
      const p0 = b.caps.find((c) => !c.chamber);
      const ay = (p0 ? p0.ay : 1.45) + shift.y;
      ray.set(new THREE.Vector3(s.x - s.dx * 9 + shift.x, s.h + ay, s.z - s.dz * 9 + shift.z), new THREE.Vector3(s.dx, 0, s.dz).normalize());
      ray.far = 40;
      const arch = ray.intersectObjects(caveMeshes, true)[0];
      return { samples, holes, holeList, onCave, onTerrain, mouthSamples, mouthOnCave, terrainMeshes: terrain.length, archFreeM: arch ? Math.round(arch.distance * 10) / 10 : null, triangles: this.stats.lastTriangles };
    };
    (globalThis as unknown as { __caves?: () => unknown }).__caves = () => ({
      seed: this.seed, caveId: atlas.caveId, built: [...this.built.keys()], pending: [...this.pending], stats: this.stats,
      // world-space walk points of every built interior (probes walk them)
      paths: Object.fromEntries([...this.built.values()].map((b) => [b.site.id, b.chambers.length ? capsPath(b) : []])),
      near: this.seed ? caveSites(this.seed).map((c) => ({ id: c.id, d: Math.hypot(c.x - atlas.localX, c.z - atlas.localZ), x: c.x, z: c.z, h: c.h, dx: c.dx, dz: c.dz })).sort((a, b) => a.d - b.d).slice(0, 5) : [],
    });
  }

  /** The id of the built cavity holding world point (x, y, z) — capsule centre — or null. */
  private cavityAt(x: number, y: number, z: number): string | null {
    for (const b of this.built.values()) {
      const lx = x - b.site.x, lz = z - b.site.z, ly = y - b.site.h;
      if (lx * lx + lz * lz > 200 * 200) continue;
      if (caveDensity({ caps: b.caps } as CavePlan, lx, ly, lz) < 0.6) return `cave:${b.site.id}`;
    }
    return null;
  }

  static optedOut(): boolean {
    try { return localStorage.getItem('atlas:caves') === 'off'; } catch { return false; }
  }

  private commonsSeed(): string | null {
    const conn = atlas.conn;
    if (!conn) return null;
    for (const w of conn.db.world.iter()) if (w.kind === 'commons') return w.terrainSeed;
    return null;
  }

  private ensureWorker(): Worker | null {
    if (this.worker) return this.worker;
    if (typeof Worker === 'undefined') return null;
    try {
      this.worker = new Worker(new URL('../workers/cave_worker.ts', import.meta.url), { type: 'module' });
      this.worker.onmessage = (e: MessageEvent<CaveResult | CaveLightResult>) => {
        const d = e.data as CaveLightResult;
        if (d && d.kind === 'light') this.onLight(d); else this.onBuilt(e.data as CaveResult);
      };
      if (this.seed) postRiverTable(this.worker, this.seed); // shared network (perf frontier item 12)
      this.worker.onerror = () => { this.worker = null; this.pending.clear(); for (const b of this.built.values()) b.lightPending = false; };
    } catch { this.worker = null; }
    return this.worker;
  }

  private tick(): void {
    if (++this.frame % TICK_EVERY !== 0) return;
    if (atlas.currentWorldId === null || atlas.currentWorldId !== atlas.commonsWorldId || AtlasCaveModule.optedOut()) {
      if (this.built.size) for (const id of [...this.built.keys()]) this.drop(id);
      if (atlas.caveId !== null) atlas.caveId = null;
      setCaveHookTarget(null);
      return;
    }
    this.lightMode = this.currentLightMode();
    if (!this.seed) {
      const s = this.commonsSeed();
      if (!s) return;
      this.seed = s;
    }
    const px = atlas.localX, pz = atlas.localZ, py = atlas.localY;
    for (const c of caveSites(this.seed)) {
      const d = Math.hypot(c.x - px, c.z - pz);
      if (d < BUILD_R && !this.built.has(c.id) && !this.pending.has(c.id)) this.request(c);
      else if (d > DROP_R && this.built.has(c.id)) this.drop(c.id);
    }
    // The physics tick decides `atlas.caveId` at the body's own position
    // (setCaveTest); here only the light follows it.
    const inside = atlas.caveId ? this.built.get(Number(atlas.caveId.slice(5))) ?? null : null;
    void py;
    this.lightTick(inside);
    if (this.light) {
      if (inside) {
        let best = inside.chambers[0], bd = Infinity;
        for (const ch of inside.chambers) { const dd = Math.hypot(ch.x + inside.site.x - px, ch.z + inside.site.z - pz); if (dd < bd) { bd = dd; best = ch; } }
        if (best) { this.light.position.set(inside.site.x + best.x, inside.site.h + best.y + best.r * 0.4, inside.site.z + best.z); this.light.intensity = 26; this.light.visible = true; }
      } else {
        // Approaching a mouth: a low light just inside the arch so the
        // entrance reads as a tunnel with depth, not a black hole.
        let nearMouth: Built | null = null, nd = MOUTH_LIGHT_M;
        for (const b of this.built.values()) { const dd = Math.hypot(b.site.x - px, b.site.z - pz); if (dd < nd) { nd = dd; nearMouth = b; } }
        if (nearMouth) {
          const p = nearMouth.caps.find((c) => !c.chamber);
          const s = nearMouth.site;
          this.light.position.set(s.x + (p ? p.bx : s.dx * 2), s.h + (p ? p.by : 1.4) + 0.6, s.z + (p ? p.bz : s.dz * 2));
          this.light.intensity = 9; this.light.visible = true;
        } else if (this.light.visible) { this.light.visible = false; this.light.intensity = 0; }
      }
    }
  }

  private request(c: CaveSite): void {
    const w = this.ensureWorker();
    if (!w) return;
    this.pending.add(c.id);
    this.stats.requested++;
    const job: CaveJob = { seed: this.seed, siteId: c.id, light: this.lightMode !== 'off' };
    if (job.light) this.requestedLight.add(c.id); else this.requestedLight.delete(c.id);
    w.postMessage(job);
  }

  private onBuilt(r: CaveResult): void {
    this.pending.delete(r.siteId);
    if (r.seed !== this.seed || this.built.has(r.siteId)) return;
    const site = caveSites(this.seed)[r.siteId];
    if (!site || r.indices.length === 0) return;
    if (Math.hypot(site.x - atlas.localX, site.z - atlas.localZ) > DROP_R) return;
    this.stats.builtMs = r.ms;
    this.stats.lastTriangles = r.indices.length / 3;
    const group = new THREE.Group();
    group.name = `cave:${site.id}`;
    group.position.set(site.x, site.h, site.z);
    group.matrixAutoUpdate = false; group.updateMatrix(); // static: the cave never moves
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(r.positions, 3));
    geo.setAttribute('normal', new THREE.BufferAttribute(r.normals, 3));
    geo.setIndex(new THREE.BufferAttribute(r.indices, 1));
    const mesh = new THREE.Mesh(geo, this.rockMat ?? new THREE.MeshStandardMaterial({ color: 0x5a5148 }));
    mesh.receiveShadow = true;
    mesh.castShadow = true;
    mesh.frustumCulled = true;
    mesh.layers.enable(RAIN_SHELTER_LAYER); // the cave rock roofs the cut (render wave 1)
    geo.computeBoundingSphere();
    group.add(mesh);
    // Crystals along the walls (every CRYSTAL_EVERY path point, offset to a wall) —
    // lib/cave_light_bake.ts crystalPlacements: the SAME placements the light bake lit the rock with.
    const crystalGeo = new THREE.IcosahedronGeometry(0.35, 0);
    const placements = crystalPlacements(r.path);
    const crystals = new THREE.InstancedMesh(crystalGeo, this.crystalMat ?? new THREE.MeshStandardMaterial({ color: 0x8fd8ff, emissive: 0x4fb8ff }), Math.max(1, placements.length));
    const m4 = new THREE.Matrix4();
    const crystalMatrices: THREE.Matrix4[] = [];
    let ci = 0;
    for (const c of placements) {
      m4.compose(new THREE.Vector3(c.x, c.y, c.z), new THREE.Quaternion().setFromEuler(new THREE.Euler(c.rx, c.ry, c.rz)), new THREE.Vector3(c.s, c.s * 1.8, c.s));
      crystals.setMatrixAt(ci++, m4);
      crystalMatrices.push(m4.clone());
    }
    crystals.count = ci;
    pinStaticMotionVectors(crystals.material as THREE.Material); // static (three's frozen previous instance matrices)
    crystals.instanceMatrix.needsUpdate = true;
    group.add(crystals);
    // Fallen rocks at the foot of the portal's front face (the outcrop itself
    // is part of the cavity mesh — it roofs the terrain hole; the old boulder
    // collar that tried to hide that hole's edge is gone).
    const boulderGeo = new THREE.IcosahedronGeometry(1, 1);
    const boulders = new THREE.InstancedMesh(boulderGeo, this.rockMat ?? new THREE.MeshStandardMaterial({ color: 0x5a5148 }), 6);
    const px = -site.dz, pz = site.dx; // perpendicular
    let bi = 0;
    for (const sgn of [-1, 1]) {
      for (let k = 0; k < 3; k++) {
        const u = -4.6 + k * 1.5;
        const v = (MOUTH_R + 2.2 + k * 0.6) * sgn;
        const x = site.dx * u + px * v, z = site.dz * u + pz * v;
        const sc = 0.55 + ((k * 31 + (sgn + 1) * 17) % 5) / 5 * 0.6;
        m4.compose(new THREE.Vector3(x, sc * 0.35, z), new THREE.Quaternion().setFromEuler(new THREE.Euler(k * 0.9, sgn * k * 1.1, k * 0.5)), new THREE.Vector3(sc, sc * 0.75, sc * 1.2));
        boulders.setMatrixAt(bi++, m4);
      }
    }
    boulders.count = bi;
    pinStaticMotionVectors(boulders.material as THREE.Material);
    boulders.instanceMatrix.needsUpdate = true;
    boulders.castShadow = true;
    group.add(boulders);
    this.scene.add(group);
    // Collider: the cavity trimesh (double-sided in Rapier), at the site origin.
    const colliderId = `cave:${site.id}`;
    this.physics.addStaticCollider(colliderId, { shape: 'trimesh_static', positions: Array.from(r.positions), indices: Array.from(r.indices) }, { x: site.x, y: site.h, z: site.z, yaw: 0, scale: 1 });
    this.built.set(site.id, { group, colliderId, caps: capsFromPath(r.path, r.chambers), chambers: r.chambers, site, mesh, crystals, boulders, crystalMatrices, light: null, lightPending: this.requestedLight.delete(site.id) });
  }

  private drop(id: number): void {
    const b = this.built.get(id);
    if (!b) return;
    this.physics.removeStaticCollider(b.colliderId);
    this.scene.remove(b.group);
    if (b.light) this.disposeLight(b.light);
    b.light = null;
    b.group.traverse((o) => {
      const m = o as THREE.Mesh;
      if (!m.isMesh) return;
      disposeLater(m.geometry);
      if ((m as THREE.InstancedMesh).isInstancedMesh) disposeLater(m as THREE.InstancedMesh);
    });
    this.built.delete(id);
    if (atlas.caveId === `cave:${id}`) { atlas.caveId = null; setCaveHookTarget(null); }
  }

  // ── CAVE LIGHT (shader gaps 2026-09-29) ─────────────────────────────────────────────────────────────────────────

  static lightOptedOut(): boolean {
    try { return localStorage.getItem('atlas:cave-light') === 'off'; } catch { return false; }
  }

  private currentLightMode(): CaveLightMode {
    if (AtlasCaveModule.lightOptedOut()) return 'off';
    const m = activePreset().caveLight;
    return m === 'off' || m === 'visibility' || m === 'baked' ? m : 'baked+probe';
  }

  /** A baked field arrived: upload it, build this cave's lit materials, warm them, swap, fade in. */
  private onLight(r: CaveLightResult): void {
    const b = this.built.get(r.siteId);
    if (b) b.lightPending = false;
    if (!b || r.seed !== this.seed || b.light || this.lightMode === 'off') return;
    this.stats.lightBaked++; this.stats.lightMs = r.ms;
    const gpu = createFieldGpu(r, r.half);
    const rock = createCaveRockMaterial(gpu, this.rockAlbedo);
    pinStaticMotionVectors(rock); // the boulders are instances (three's frozen previous instance matrices)
    const crystal = createCaveCrystalMaterial(gpu, null, CRYSTAL_EMISSIVE_HEX, CRYSTAL_EMISSIVE_INTENSITY);
    pinStaticMotionVectors(crystal);
    const s = b.site;
    const lit: LitCave = {
      gpu, originAbs: [s.x + r.origin[0], s.h + r.origin[1], s.z + r.origin[2]],
      view: halfFieldView({ origin: r.origin, spacing: r.spacing, dims: r.dims, half: r.half }),
      rock, crystal, probe: null, probeState: 'none', probeWaited: 0,
      mix: 0, local: this.lightMode === 'visibility' ? 0 : 1, probeW: 0, warmed: false,
      bakeMs: r.ms, captureMs: 0, stats: r.stats,
    };
    b.light = lit;
    this.writeLightUniforms(lit);
    void this.warmThenSwap(b, lit, [[b.mesh, rock], [b.boulders, rock], [b.crystals, crystal]]).then(() => {
      if (b.light !== lit) return;
      b.mesh.material = rock; b.boulders.material = rock; b.crystals.material = crystal;
      lit.warmed = true;
    });
  }

  /**
   * Compile a cave's new materials BEFORE they draw: a proxy of each (mesh, material) pair — the same geometry, the
   * same instance count (three folds count > 1 into the render object's key), never frustum-culled — through
   * compileAsync against the live scene's lights, bounded by WARM_TIMEOUT_MS. The real meshes keep the plain rock
   * meanwhile, so nothing blinks.
   */
  private async warmThenSwap(b: Built, lit: LitCave, pairs: Array<[THREE.Mesh, THREE.Material]>): Promise<void> {
    const r = this.app?.renderer as unknown as { native?: { compileAsync?: (o: unknown, c: unknown, t?: unknown) => Promise<unknown> }; cameras?: { main?: unknown } } | undefined;
    if (!r?.native?.compileAsync || !r.cameras?.main) return;
    const proxy = new THREE.Group();
    proxy.position.copy(b.group.position);
    for (const [m, mat] of pairs) {
      const inst = m as THREE.InstancedMesh;
      const p = inst.isInstancedMesh ? new THREE.InstancedMesh(m.geometry, mat, inst.count) : new THREE.Mesh(m.geometry, mat);
      if ((p as THREE.InstancedMesh).isInstancedMesh) { (p as THREE.InstancedMesh).instanceMatrix = inst.instanceMatrix; (p as THREE.InstancedMesh).count = inst.count; }
      p.frustumCulled = false;
      proxy.add(p);
    }
    proxy.updateMatrixWorld(true);
    void lit;
    try {
      await Promise.race([r.native.compileAsync(proxy, r.cameras.main, this.scene), new Promise((res) => setTimeout(res, WARM_TIMEOUT_MS))]);
    } catch { /* a failed warm still swaps — the plain path never waits forever */ }
  }

  /** Per frame: the fades and the render-space uniforms (the FP64 rebase moves them). */
  private lightFrame(): void {
    const now = performance.now();
    const dt = this.lastFrameMs ? Math.min(0.25, (now - this.lastFrameMs) / 1000) : 0;
    this.lastFrameMs = now;
    const mode = this.lightMode;
    const k = dt / FIELD_FADE_S;
    for (const b of this.built.values()) {
      const L = b.light;
      if (!L) continue;
      const wantMix = mode === 'off' || !L.warmed ? 0 : 1;
      const wantLocal = mode === 'visibility' ? 0 : 1;
      const wantProbe = mode === 'baked+probe' && L.probe ? 1 : 0;
      L.mix = approach(L.mix, wantMix, k);
      L.local = approach(L.local, wantLocal, k);
      L.probeW = approach(L.probeW, wantProbe, k);
      this.writeLightUniforms(L);
      if (mode === 'off' && L.warmed && L.mix === 0) this.unlight(b);
    }
  }

  private writeLightUniforms(L: LitCave): void {
    rebaseCaveUniforms(L.gpu, L.originAbs, L.probe, this.scene.position);
    (L.gpu.uMix as { value: number }).value = L.mix;
    (L.gpu.uLocal as { value: number }).value = L.local;
    (L.gpu.uProbe as { value: number }).value = L.probeW;
  }

  /** The heavy tick: bakes to request, the probe capture, the hook's target at the player. */
  private lightTick(inside: Built | null): void {
    const mode = this.lightMode;
    for (const b of this.built.values()) {
      if (mode !== 'off' && !b.light && !b.lightPending && this.worker) {
        b.lightPending = true;
        const job: CaveJob = { seed: this.seed, siteId: b.site.id, light: true, lightOnly: true };
        this.worker.postMessage(job);
      }
      const L = b.light;
      if (L && mode === 'baked+probe' && L.warmed && L.probeState === 'none' && L.mix > 0.5) {
        if (!this.rockAlbedo && ++L.probeWaited < CAPTURE_WAIT_TICKS) continue;
        this.captureProbe(b, L);
      }
    }
    // THE HOOK: the field at the player while inside; the identity elsewhere (eased in LightingModule's frame)
    const L = inside?.light;
    if (inside && L && L.mix > 0) {
      const s = inside.site;
      const t = hookTargetAt(L.view, atlas.localX - s.x, atlas.localY + 1.0 - s.h, atlas.localZ - s.z);
      const m = L.mix, loc = L.local;
      setCaveHookTarget({ mul: 1 - m * (1 - t.mul), tint: [t.tint[0] * m * loc, t.tint[1] * m * loc, t.tint[2] * m * loc] });
    } else setCaveHookTarget(null);
  }

  /** One capture per cave: its local radiance → a cube → PMREM; the crystals then take a material that reads it. */
  private captureProbe(b: Built, L: LitCave): void {
    L.probeState = 'pending';
    const renderer = this.app?.renderer.native as unknown as THREE.WebGPURenderer | undefined;
    const ch = [...b.chambers].sort((a, c) => c.r - a.r)[0];
    if (!renderer || !ch) { L.probeState = 'failed'; return; }
    try {
      const floor = ch.y - 1.25; // the cave's one floor plane (cave_gen FLOOR_DROP)
      const center = new THREE.Vector3(ch.x, floor + 1.7, ch.z);
      const boxMin = new THREE.Vector3(ch.x - ch.r, floor, ch.z - ch.r), boxMax = new THREE.Vector3(ch.x + ch.r, ch.y + ch.r, ch.z + ch.r);
      const cap = captureCaveProbe({
        renderer, field: L.gpu, fieldOriginLocal: [L.originAbs[0] - b.site.x, L.originAbs[1] - b.site.h, L.originAbs[2] - b.site.z],
        geometry: b.mesh.geometry, crystalGeometry: b.crystals.geometry, crystalMatrices: b.crystalMatrices,
        albedo: this.rockAlbedo, crystalRadiance: crystalRadiance(), center, boxMin, boxMax,
      });
      const off = new THREE.Vector3(b.site.x, b.site.h, b.site.z);
      const probe = createProbeGpu(cap.pmrem, { min: boxMin.add(off), max: boxMax.add(off), center: center.add(off) });
      const crystal = createCaveCrystalMaterial(L.gpu, probe, CRYSTAL_EMISSIVE_HEX, CRYSTAL_EMISSIVE_INTENSITY);
      pinStaticMotionVectors(crystal);
      L.captureMs = cap.ms;
      void this.warmThenSwap(b, L, [[b.crystals, crystal]]).then(() => {
        if (b.light !== L) { disposeLater(crystal); probe.dispose(); return; }
        const old = L.crystal;
        L.probe = probe; L.crystal = crystal; L.probeState = 'done';
        this.writeLightUniforms(L);
        if (b.crystals.material === old) b.crystals.material = crystal;
        disposeLater(old);
      });
    } catch (e) {
      L.probeState = 'failed';
      // eslint-disable-next-line no-console
      console.warn('[CaveLight] probe capture failed', e);
    }
  }

  /** Back to the plain materials (the flag went off and the fade finished). */
  private unlight(b: Built): void {
    const L = b.light;
    if (!L) return;
    b.mesh.material = this.rockMat ?? b.mesh.material;
    b.boulders.material = this.rockMat ?? b.boulders.material;
    b.crystals.material = this.crystalMat ?? b.crystals.material;
    b.light = null;
    this.disposeLight(L);
  }

  private disposeLight(L: LitCave): void {
    disposeLater(L.rock); disposeLater(L.crystal);
    for (const t of L.gpu.textures) disposeLater(t);
    if (L.probe) disposeLater({ dispose: () => L.probe?.dispose() });
  }

  private installLightLevers(): void {
    const g = globalThis as unknown as Record<string, unknown>;
    g.__caveLight = () => {
      const inside = atlas.caveId ? this.built.get(Number(atlas.caveId.slice(5))) ?? null : null;
      let atPlayer: unknown = null;
      if (inside?.light) {
        const s = inside.site;
        const t = hookTargetAt(inside.light.view, atlas.localX - s.x, atlas.localY + 1.0 - s.h, atlas.localZ - s.z);
        atPlayer = { cave: s.id, skyTransfer: +t.mul.toFixed(4), localTint: t.tint.map((v) => +v.toExponential(3)) };
      }
      return {
        mode: this.lightMode, optedOut: AtlasCaveModule.lightOptedOut(),
        hook: { targetMul: +caveHookState.targetMul.toFixed(4), mul: +caveHookState.mul.toFixed(4), tint: caveHookState.tint.map((v) => +v.toExponential(3)), applied: { ...caveHookState.applied } },
        atPlayer,
        caves: [...this.built.values()].map((b) => ({
          id: b.site.id, pending: b.lightPending, lit: !!b.light,
          probes: b.light?.stats.probes ?? 0, traced: b.light?.stats.traced ?? 0, relocated: b.light?.stats.relocated ?? 0,
          inactive: b.light?.stats.inactive ?? 0, open: b.light?.stats.open ?? 0, rays: b.light?.stats.rays ?? 0,
          dims: b.light ? [...b.light.gpu.field.dims] : null, spacing: b.light?.gpu.field.spacing ?? null,
          bakeMs: b.light ? Math.round(b.light.bakeMs) : null, bytes: b.light?.gpu.bytes ?? 0,
          mix: b.light ? +b.light.mix.toFixed(3) : 0, local: b.light ? +b.light.local.toFixed(3) : 0, probeW: b.light ? +b.light.probeW.toFixed(3) : 0,
          probe: b.light?.probeState ?? 'none', captureMs: b.light ? Math.round(b.light.captureMs) : null, warmed: b.light?.warmed ?? false,
          materials: { rock: (b.mesh.material as THREE.Material).name || (b.mesh.material as THREE.Material).type, crystal: (b.crystals.material as THREE.Material).name || (b.crystals.material as THREE.Material).type },
        })),
        materialStats: { ...caveLightMaterialStats }, bakes: this.stats.lightBaked, lastBakeMs: Math.round(this.stats.lightMs),
      };
    };
    /** Sample a built cave's field at an ABSOLUTE point for a normal → { T, E }. */
    g.__caveLightSample = (id: number, x: number, y: number, z: number, nx = 0, ny = 1, nz = 0) => {
      const b = this.built.get(id);
      if (!b?.light) return null;
      const s = b.site;
      const o = sampleField(b.light.view, x - s.x, y - s.h, z - s.z, nx, ny, nz);
      return { T: +o[3].toFixed(4), E: [o[0], o[1], o[2]].map((v) => +v.toExponential(3)) };
    };
  }

  dispose(): void {
    for (const id of [...this.built.keys()]) this.drop(id);
    this.worker?.terminate();
    this.worker = null;
    this.physics.setCaveTest(null);
    if (this.light) { this.scene.remove(this.light); this.light = null; }
    setCaveHookTarget(null);
  }
}

/** World-space walk points (capsule ends) of a built interior. */
function capsPath(b: Built): Array<{ x: number; y: number; z: number }> {
  const out: Array<{ x: number; y: number; z: number }> = [];
  for (const c of b.caps) if (!c.chamber) out.push({ x: b.site.x + c.bx, y: b.site.h + c.by, z: b.site.z + c.bz });
  return out;
}

/** Rebuild the inside test's capsules from the path the worker sends back. */
function capsFromPath(path: Array<{ x: number; y: number; z: number; r: number }>, chambers: Array<{ x: number; y: number; z: number; r: number }>): CaveCapsule[] {
  const caps: CaveCapsule[] = [];
  for (let i = 1; i < path.length; i++) {
    const a = path[i - 1], b = path[i];
    caps.push({ ax: a.x, ay: a.y, az: a.z, bx: b.x, by: b.y, bz: b.z, r: Math.min(a.r, b.r, 3.4) });
  }
  for (const c of chambers) caps.push({ ax: c.x, ay: c.y, az: c.z, bx: c.x, by: c.y + 0.01, bz: c.z, r: c.r, chamber: true });
  return caps;
}

/** Move toward a target by at most `step` (a linear fade that lands exactly). */
function approach(v: number, target: number, step: number): number {
  if (v < target) return Math.min(target, v + step);
  if (v > target) return Math.max(target, v - step);
  return v;
}
